import React, { useState, useEffect, useCallback, useRef } from 'react';
import { jobReportsService } from '../services';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { 
  Upload, FileText, Clock, User, Eye, History, 
  Loader2, AlertCircle, CheckCircle, File, Lock,
  Send, ThumbsUp, EyeOff, Settings, Info, Download
} from 'lucide-react';
import { format } from 'date-fns';
import { getApiError } from '@/lib/utils';

// Constants
const SERVICE_TYPES = [
  { value: 'food', label: 'Food' },
  { value: 'beverage', label: 'Beverage' },
  { value: 'shop', label: 'Shop' }
];

// Active report types available for upload
// Note: 'audit' (Audit Report) - DISABLED: Auto-generated by wizard
// Note: 'dashboard' (Dashboard Report) - DISABLED: No longer used
const REPORT_TYPES = [
  { value: 'stock', label: 'Stock Audit Report', pdfOnly: true },
  { value: 'ps', label: 'PS File', pdfOnly: false, internalOnly: true }
];

// Status configuration with colors
const STATUS_CONFIG = {
  draft: { 
    label: 'Draft', 
    variant: 'secondary', 
    icon: FileText,
    className: 'bg-gray-100 text-gray-800 border-gray-300'
  },
  pending_approval: { 
    label: 'Pending Approval', 
    variant: 'outline', 
    icon: Clock,
    className: 'bg-yellow-100 text-yellow-800 border-yellow-300'
  },
  published: { 
    label: 'Published', 
    variant: 'default', 
    icon: CheckCircle,
    className: 'bg-green-100 text-green-800 border-green-300'
  },
  unpublished: { 
    label: 'Unpublished', 
    variant: 'destructive', 
    icon: EyeOff,
    className: 'bg-red-100 text-red-800 border-red-300'
  }
};

export default function ClientReportsSection({ jobId, job, onUpdate }) {
  const { isAdmin, isScheduler, isAuditor, canDirectlyPublishReports, user } = useAuth();
  const { toast } = useToast();
  
  // State
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [versionsDialogOpen, setVersionsDialogOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  // Authenticated download — uses the repository download endpoint (proxied, same as SS107 list view)
  const handleFileDownload = useCallback(async (versionId, filename) => {
    try {
      const response = await api.get(`/reports/repository/version/${versionId}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || 'report.pdf');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast({ title: 'Download failed', description: getApiError(error, 'Could not download file'), variant: 'destructive' });
    }
  }, [toast]);
  
  // Workflow settings state
  const [workflowSettings, setWorkflowSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsError, setSettingsError] = useState(null);
  
  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    serviceType: '',
    reportType: '',
    notes: '',
    file: null,
    saveAsDraft: false
  });
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  
  // Derived values
  const availableServiceTypes = SERVICE_TYPES;
  const autoSelectServiceType = availableServiceTypes.length === 1;
  
  // Permission checks
  // Admins and Schedulers can always upload
  // Auditors can upload if they are assigned to the job (check both auditors and assignments arrays)
  const isAssignedAuditor = isAuditor && (
    job?.auditors?.some(a => a.user_id === user?.id) ||
    job?.assignments?.some(a => a.auditor_id === user?.id || a.auditor?.user_id === user?.id) ||
    job?.participants?.some(p => p.auditor?.user_id === user?.id)
  );
  const canUpload = isAdmin || isScheduler || isAssignedAuditor;
  const isAdminOrScheduler = isAdmin || isScheduler;
  // iter416 — Trusted auditors flagged with `can_directly_publish_reports`
  // act like Admin/Scheduler for publish actions (matches backend iter404).
  // Without this, Sophie Gardner (auditor + flag) saw only "Request Publish"
  // and her reports went through approval despite the flag being on.
  const canPublishDirectly = isAdminOrScheduler || canDirectlyPublishReports;
  
  // Workflow mode helpers
  const isApprovalMode = workflowSettings?.reports_publish_mode === 'approval_required';
  const isDirectMode = workflowSettings?.reports_publish_mode === 'direct' || !workflowSettings;
  const allowDraftUploads = workflowSettings?.reports_allow_draft_uploads === true;

  // Load workflow settings
  const loadWorkflowSettings = useCallback(async () => {
    try {
      setSettingsLoading(true);
      setSettingsError(null);
      const data = await jobReportsService.getWorkflowSettings();
      setWorkflowSettings(data);
    } catch (err) {
      console.error('Failed to load workflow settings:', err);
      setSettingsError('Failed to load workflow settings');
      // Use safe defaults
      setWorkflowSettings({
        reports_publish_mode: 'direct',
        reports_allow_draft_uploads: false
      });
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  // Load reports
  const loadReports = useCallback(async () => {
    try {
      setLoading(true);
      const data = await jobReportsService.listForJob(jobId);
      setReports(data.reports || []);
    } catch (err) {
      toast({
        title: 'Error',
        description: getApiError(err, 'Failed to load reports'),
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [jobId, toast]);

  // Initial load
  useEffect(() => {
    loadWorkflowSettings();
    loadReports();
  }, [loadWorkflowSettings, loadReports]);

  // Get report by type
  const getReportByType = (serviceType, reportType) => {
    return reports.find(r => r.service_type === serviceType && r.report_type === reportType);
  };

  // Get status with safe default
  const getReportStatus = (report) => {
    if (!report) return null;
    return report.current_status || 'unpublished';
  };

  // File handling
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (file) => {
    const reportTypeConfig = REPORT_TYPES.find(rt => rt.value === uploadForm.reportType);
    if (reportTypeConfig?.pdfOnly && file.type !== 'application/pdf') {
      toast({
        title: 'Invalid File Type',
        description: `${reportTypeConfig.label} requires a PDF file.`,
        variant: 'destructive'
      });
      return;
    }
    setUploadForm(f => ({ ...f, file }));
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  // Open upload dialog
  const openUploadDialog = (serviceType = '', reportType = '') => {
    setUploadForm({
      serviceType: autoSelectServiceType ? availableServiceTypes[0].value : serviceType,
      reportType,
      notes: '',
      file: null,
      saveAsDraft: false
    });
    setUploadDialogOpen(true);
  };

  // Handle upload
  const handleUpload = async () => {
    if (!uploadForm.file || !uploadForm.serviceType || !uploadForm.reportType) {
      toast({
        title: 'Missing Information',
        description: 'Please select service type, report type, and file.',
        variant: 'destructive'
      });
      return;
    }

    setUploading(true);
    try {
      const reportTypeConfig = REPORT_TYPES.find(rt => rt.value === uploadForm.reportType);
      const visibility = reportTypeConfig?.internalOnly ? 'internal_only' : 'internal_only';
      
      // Only pass saveAsDraft if drafts are allowed
      const saveAsDraft = allowDraftUploads ? uploadForm.saveAsDraft : false;
      
      const newReport = await jobReportsService.upload(
        jobId,
        uploadForm.file,
        uploadForm.serviceType,
        uploadForm.reportType,
        visibility,
        uploadForm.notes || null,
        saveAsDraft
      );
      
      // Update local state immediately
      setReports(prev => {
        const existingIndex = prev.findIndex(
          r => r.service_type === newReport.service_type && r.report_type === newReport.report_type
        );
        if (existingIndex >= 0) {
          const updated = [...prev];
          updated[existingIndex] = newReport;
          return updated;
        } else {
          return [...prev, newReport];
        }
      });
      
      const statusLabel = STATUS_CONFIG[newReport.current_status]?.label || newReport.current_status;
      toast({
        title: 'Upload Successful',
        description: `Report uploaded (v${newReport.current_version?.version_number || 1}) - Status: ${statusLabel}`
      });
      
      setUploadDialogOpen(false);
      if (onUpdate) onUpdate();
    } catch (err) {
      toast({
        title: 'Upload Failed',
        description: getApiError(err, 'Failed to upload report'),
        variant: 'destructive'
      });
    } finally {
      setUploading(false);
    }
  };

  // View versions
  const openVersionsDialog = async (report) => {
    setSelectedReport(report);
    setVersionsDialogOpen(true);
    setVersionsLoading(true);
    try {
      const versionsData = await jobReportsService.getVersions(report.id);
      setVersions(versionsData);
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to load version history',
        variant: 'destructive'
      });
    } finally {
      setVersionsLoading(false);
    }
  };

  // Update report in local state
  const updateReportInState = (updatedReport) => {
    setReports(prev => prev.map(r => r.id === updatedReport.id ? updatedReport : r));
  };

  // Handle publish
  const handlePublish = async (report) => {
    setActionLoading(report.id);
    try {
      const updatedReport = await jobReportsService.publish(report.id);
      updateReportInState(updatedReport);
      toast({ title: 'Success', description: 'Report published successfully' });
    } catch (err) {
      toast({
        title: 'Publish Failed',
        description: getApiError(err, 'Failed to publish report'),
        variant: 'destructive'
      });
    } finally {
      setActionLoading(null);
    }
  };

  // Handle unpublish
  const handleUnpublish = async (report) => {
    setActionLoading(report.id);
    try {
      const updatedReport = await jobReportsService.unpublish(report.id);
      updateReportInState(updatedReport);
      toast({ title: 'Success', description: 'Report unpublished successfully' });
    } catch (err) {
      toast({
        title: 'Unpublish Failed',
        description: getApiError(err, 'Failed to unpublish report'),
        variant: 'destructive'
      });
    } finally {
      setActionLoading(null);
    }
  };

  // Handle request publish
  const handleRequestPublish = async (report) => {
    setActionLoading(report.id);
    try {
      const updatedReport = await jobReportsService.requestPublish(report.id);
      updateReportInState(updatedReport);
      toast({ title: 'Request Submitted', description: 'Publish request sent for approval' });
    } catch (err) {
      toast({
        title: 'Request Failed',
        description: getApiError(err, 'Failed to request publish'),
        variant: 'destructive'
      });
    } finally {
      setActionLoading(null);
    }
  };

  // Handle approve publish
  const handleApprovePublish = async (report) => {
    setActionLoading(report.id);
    try {
      const updatedReport = await jobReportsService.approvePublish(report.id);
      updateReportInState(updatedReport);
      toast({ title: 'Approved', description: 'Report approved and published' });
    } catch (err) {
      toast({
        title: 'Approval Failed',
        description: getApiError(err, 'Failed to approve report'),
        variant: 'destructive'
      });
    } finally {
      setActionLoading(null);
    }
  };

  // Render status badge
  const renderStatusBadge = (report) => {
    if (!report) return null;
    
    const status = getReportStatus(report);
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.unpublished;
    const StatusIcon = config.icon;
    
    return (
      <Badge 
        variant={config.variant}
        className={`${config.className} font-medium`}
        data-testid={`status-badge-${report.id}`}
      >
        <StatusIcon className="h-3 w-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  // Render action buttons based on workflow mode, permissions, and status
  const renderActionButtons = (report, reportTypeConfig) => {
    // PS files never have publish actions
    if (!report || reportTypeConfig.internalOnly) {
      return null;
    }
    
    const isLoading = actionLoading === report.id;
    const status = getReportStatus(report);
    
    const buttons = [];

    // DIRECT MODE
    if (isDirectMode) {
      // UNPUBLISHED or DRAFT -> Publish (anyone with upload access)
      if ((status === 'unpublished' || status === 'draft') && canUpload) {
        buttons.push(
          <Button
            key="publish"
            variant="default"
            size="sm"
            onClick={() => handlePublish(report)}
            disabled={isLoading}
            className="bg-green-600 hover:bg-green-700"
            data-testid={`publish-btn-${report.id}`}
          >
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle className="h-4 w-4 mr-1" />}
            Publish
          </Button>
        );
      }
      
      // PUBLISHED -> Unpublish (Admin/Scheduler only)
      if (status === 'published' && isAdminOrScheduler) {
        buttons.push(
          <Button
            key="unpublish"
            variant="outline"
            size="sm"
            onClick={() => handleUnpublish(report)}
            disabled={isLoading}
            data-testid={`unpublish-btn-${report.id}`}
          >
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <EyeOff className="h-4 w-4 mr-1" />}
            Unpublish
          </Button>
        );
      }
    }
    
    // APPROVAL_REQUIRED MODE
    if (isApprovalMode) {
      // Auditor (not admin/scheduler AND not trusted): UNPUBLISHED/DRAFT -> Request Publish
      if ((status === 'unpublished' || status === 'draft') && isAuditor && !canPublishDirectly) {
        buttons.push(
          <Button
            key="request"
            variant="outline"
            size="sm"
            onClick={() => handleRequestPublish(report)}
            disabled={isLoading}
            className="border-yellow-500 text-yellow-700 hover:bg-yellow-50"
            data-testid={`request-publish-btn-${report.id}`}
          >
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
            Request Publish
          </Button>
        );
      }
      
      // Admin/Scheduler/Trusted auditor: UNPUBLISHED/DRAFT -> Can directly publish
      if ((status === 'unpublished' || status === 'draft') && canPublishDirectly) {
        buttons.push(
          <Button
            key="publish"
            variant="default"
            size="sm"
            onClick={() => handlePublish(report)}
            disabled={isLoading}
            className="bg-green-600 hover:bg-green-700"
            data-testid={`publish-btn-${report.id}`}
          >
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle className="h-4 w-4 mr-1" />}
            Publish
          </Button>
        );
      }
      
      // Admin/Scheduler/Trusted auditor: PENDING_APPROVAL -> Approve & Publish
      if (status === 'pending_approval' && canPublishDirectly) {
        buttons.push(
          <Button
            key="approve"
            variant="default"
            size="sm"
            onClick={() => handleApprovePublish(report)}
            disabled={isLoading}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid={`approve-btn-${report.id}`}
          >
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <ThumbsUp className="h-4 w-4 mr-1" />}
            Approve & Publish
          </Button>
        );
      }
      
      // Auditor without direct-publish flag: PENDING_APPROVAL -> Show waiting message (no button)
      if (status === 'pending_approval' && isAuditor && !canPublishDirectly) {
        buttons.push(
          <span key="waiting" className="text-sm text-yellow-600 italic flex items-center">
            <Clock className="h-4 w-4 mr-1" />
            Awaiting approval...
          </span>
        );
      }
      
      // Admin/Scheduler: PUBLISHED -> Unpublish
      if (status === 'published' && isAdminOrScheduler) {
        buttons.push(
          <Button
            key="unpublish"
            variant="outline"
            size="sm"
            onClick={() => handleUnpublish(report)}
            disabled={isLoading}
            data-testid={`unpublish-btn-${report.id}`}
          >
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <EyeOff className="h-4 w-4 mr-1" />}
            Unpublish
          </Button>
        );
      }
    }
    
    return buttons.length > 0 ? (
      <div className="flex flex-wrap gap-2 mt-2" data-testid={`action-buttons-${report.id}`}>
        {buttons}
      </div>
    ) : null;
  };

  // Render report slot
  const renderReportSlot = (serviceType, reportTypeConfig) => {
    const report = getReportByType(serviceType, reportTypeConfig.value);
    
    return (
      <div 
        key={`${serviceType}-${reportTypeConfig.value}`}
        className="p-4 border rounded-lg bg-card hover:bg-accent/30 transition-colors"
        data-testid={`report-slot-${serviceType}-${reportTypeConfig.value}`}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            {/* Report Type Header */}
            <div className="flex items-center gap-2 mb-2">
              <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <span className="font-medium">{reportTypeConfig.label}</span>
              {reportTypeConfig.internalOnly && (
                <Badge variant="secondary" className="text-xs bg-purple-100 text-purple-800 border-purple-300">
                  <Lock className="h-3 w-3 mr-1" />
                  Internal Only
                </Badge>
              )}
            </div>
            
            {report ? (
              <div className="space-y-2">
                {/* Current version info — compact single row */}
                {report.current_version && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground flex-wrap">
                    <File className="h-3 w-3 shrink-0" />
                    <span className="truncate max-w-[200px]" title={report.current_version.original_filename}>
                      {report.current_version.original_filename}
                    </span>
                    <Badge variant="outline" className="text-xs">v{report.current_version.version_number}</Badge>
                    <span className="text-xs">{report.current_version.uploaded_by_name || ''}</span>
                    <span className="text-xs">{format(new Date(report.current_version.uploaded_at), 'd MMM yyyy')}</span>
                  </div>
                )}
                {/* Status + action buttons — single compact row */}
                <div className="flex items-center gap-2 flex-wrap">
                  {renderStatusBadge(report)}
                  {report.visibility_scope === 'internal_only' && !reportTypeConfig.internalOnly && (
                    <Badge variant="secondary" className="text-xs">
                      <Lock className="h-3 w-3 mr-0.5" /> Internal
                    </Badge>
                  )}
                  {renderActionButtons(report, reportTypeConfig)}
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No report uploaded</p>
            )}
          </div>
          
          {/* Right-side buttons — compact inline */}
          <div className="flex items-center gap-1.5 shrink-0">
            {report?.current_version?.file_storage_key && (
              <Button variant="outline" size="sm"
                onClick={() => handleFileDownload(report.current_version.id, report.current_version.original_filename)}
                data-testid={`download-btn-${serviceType}-${reportTypeConfig.value}`}
                title="Download current version"
              >
                <Download className="h-4 w-4" />
              </Button>
            )}
            {report && (
              <Button variant="ghost" size="sm"
                onClick={() => openVersionsDialog(report)}
                data-testid={`versions-btn-${serviceType}-${reportTypeConfig.value}`}
                title="Version history"
              >
                <History className="h-4 w-4" />
              </Button>
            )}
            {canUpload && (
              <Button variant={report ? 'outline' : 'default'} size="sm"
                onClick={() => openUploadDialog(serviceType, reportTypeConfig.value)}
                data-testid={`upload-btn-${serviceType}-${reportTypeConfig.value}`}
              >
                <Upload className="h-4 w-4 mr-1" />
                {report ? 'New' : 'Upload'}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Loading state
  if (loading || settingsLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground mr-2" />
          <span className="text-muted-foreground">Loading reports...</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card data-testid="client-reports-section">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Client Reports</CardTitle>
              <CardDescription>Upload and manage reports for this job</CardDescription>
            </div>
            {canUpload && (
              <Button onClick={() => openUploadDialog()} data-testid="upload-report-btn">
                <Upload className="h-4 w-4 mr-2" />
                Upload Report
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {/* Workflow Settings Banner */}
          <Alert className="mb-6 bg-slate-50 border-slate-200" data-testid="workflow-settings-banner">
            <Settings className="h-4 w-4" />
            <AlertDescription>
              <div className="flex flex-wrap items-center gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-700">Publish Mode:</span>
                  <Badge 
                    variant={isApprovalMode ? 'outline' : 'default'}
                    className={isApprovalMode 
                      ? 'bg-yellow-100 text-yellow-800 border-yellow-300' 
                      : 'bg-green-100 text-green-800 border-green-300'
                    }
                    data-testid="publish-mode-badge"
                  >
                    {isApprovalMode ? 'Approval Required' : 'Direct'}
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-700">Draft Uploads:</span>
                  <Badge 
                    variant={allowDraftUploads ? 'default' : 'secondary'}
                    className={allowDraftUploads 
                      ? 'bg-blue-100 text-blue-800 border-blue-300' 
                      : 'bg-gray-100 text-gray-600 border-gray-300'
                    }
                    data-testid="draft-uploads-badge"
                  >
                    {allowDraftUploads ? 'Enabled' : 'Disabled'}
                  </Badge>
                </div>
                {settingsError && (
                  <span className="text-xs text-red-500 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {settingsError}
                  </span>
                )}
              </div>
            </AlertDescription>
          </Alert>

          {/* Reports grouped by Service Type */}
          <div className="space-y-6">
            {SERVICE_TYPES.map(serviceType => (
              <div key={serviceType.value}>
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <span className={`w-3 h-3 rounded-full ${serviceType.value === 'food' ? 'bg-green-500' : 'bg-blue-500'}`} />
                  {serviceType.label}
                </h3>
                <div className="grid gap-3 md:grid-cols-2">
                  {REPORT_TYPES.map(reportType => renderReportSlot(serviceType.value, reportType))}
                </div>
              </div>
            ))}
          </div>
          
          {reports.length === 0 && !canUpload && (
            <div className="text-center py-8">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No reports uploaded yet</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Upload Report</DialogTitle>
            <DialogDescription>
              {uploadForm.reportType && REPORT_TYPES.find(rt => rt.value === uploadForm.reportType)?.pdfOnly
                ? 'Upload a PDF file'
                : 'Upload a file for this report'
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4 overflow-y-auto flex-1 min-h-0 pr-1">
            {/* Service Type */}
            {!autoSelectServiceType && (
              <div className="space-y-2">
                <Label>Service Type *</Label>
                <Select
                  value={uploadForm.serviceType}
                  onValueChange={(v) => setUploadForm(f => ({ ...f, serviceType: v }))}
                >
                  <SelectTrigger data-testid="upload-service-type">
                    <SelectValue placeholder="Select service type" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableServiceTypes.map(st => (
                      <SelectItem key={st.value} value={st.value}>{st.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Report Type */}
            <div className="space-y-2">
              <Label>Report Type *</Label>
              <Select
                value={uploadForm.reportType}
                onValueChange={(v) => {
                  setUploadForm(f => ({ ...f, reportType: v, file: null }));
                }}
              >
                <SelectTrigger data-testid="upload-report-type">
                  <SelectValue placeholder="Select report type" />
                </SelectTrigger>
                <SelectContent>
                  {REPORT_TYPES.map(rt => (
                    <SelectItem key={rt.value} value={rt.value}>
                      {rt.label} {rt.pdfOnly && '(PDF only)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* File Drop Zone */}
            <div className="space-y-2">
              <Label>File *</Label>
              <div
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${
                  dragActive ? 'border-primary bg-primary/10' : 'border-muted-foreground/25 hover:border-primary/50'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                data-testid="upload-drop-zone"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept={uploadForm.reportType && REPORT_TYPES.find(rt => rt.value === uploadForm.reportType)?.pdfOnly 
                    ? 'application/pdf' 
                    : '*/*'
                  }
                  onChange={handleFileInputChange}
                  data-testid="upload-file-input"
                />
                {uploadForm.file ? (
                  <div className="space-y-2 max-w-full overflow-hidden">
                    <File className="h-8 w-8 mx-auto text-primary" />
                    <p className="font-medium text-sm truncate max-w-[250px] mx-auto" title={uploadForm.file.name}>
                      {uploadForm.file.name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {(uploadForm.file.size / 1024).toFixed(1)} KB
                    </p>
                    <Button variant="link" size="sm" onClick={(e) => {
                      e.stopPropagation();
                      setUploadForm(f => ({ ...f, file: null }));
                    }}>
                      Remove
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Upload className="h-8 w-8 mx-auto text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">
                      Drag & drop or click to select
                    </p>
                    {uploadForm.reportType && REPORT_TYPES.find(rt => rt.value === uploadForm.reportType)?.pdfOnly && (
                      <p className="text-xs text-muted-foreground">PDF files only</p>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            {/* Save as Draft toggle - ONLY shown when drafts are enabled */}
            {allowDraftUploads && (
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200" data-testid="draft-toggle-section">
                <div className="space-y-0.5">
                  <Label htmlFor="save-as-draft" className="text-sm font-medium text-blue-900">Save as Draft</Label>
                  <p className="text-xs text-blue-700">
                    Upload without finalizing. You can publish later.
                  </p>
                </div>
                <Switch
                  id="save-as-draft"
                  checked={uploadForm.saveAsDraft}
                  onCheckedChange={(checked) => setUploadForm(f => ({ ...f, saveAsDraft: checked }))}
                  data-testid="save-as-draft-toggle"
                />
              </div>
            )}
            
            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea
                value={uploadForm.notes}
                onChange={(e) => setUploadForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Add notes about this version..."
                rows={2}
                data-testid="upload-notes"
              />
            </div>
            
            {/* PS File Warning */}
            {uploadForm.reportType === 'ps' && (
              <div className="flex items-center gap-2 p-3 bg-purple-50 rounded-lg border border-purple-200 text-sm">
                <Lock className="h-4 w-4 text-purple-600" />
                <span className="text-purple-800">PS Files are always internal-only and cannot be published.</span>
              </div>
            )}
            
            {/* Status Info when drafts are disabled */}
            {!allowDraftUploads && (
              <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg border border-slate-200 text-sm" data-testid="no-draft-info">
                <Info className="h-4 w-4 text-slate-600" />
                <span className="text-slate-700">Reports are uploaded as final. Status will be "Unpublished" until you publish.</span>
              </div>
            )}
          </div>
          
          <DialogFooter className="flex-shrink-0">
            <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpload} 
              disabled={!uploadForm.file || !uploadForm.serviceType || !uploadForm.reportType || uploading}
              data-testid="confirm-upload-btn"
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Version History Dialog */}
      <Dialog open={versionsDialogOpen} onOpenChange={setVersionsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Version History</DialogTitle>
            <DialogDescription>
              {selectedReport && (
                <span>
                  {SERVICE_TYPES.find(st => st.value === selectedReport.service_type)?.label}{' '}
                  - {REPORT_TYPES.find(rt => rt.value === selectedReport.report_type)?.label}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {versionsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : versions.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No versions found</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Version</TableHead>
                    <TableHead>Filename</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Uploaded By</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {versions.map((v) => (
                    <TableRow key={v.id} data-testid={`version-row-${v.id}`}>
                      <TableCell>
                        <Badge variant="outline">v{v.version_number}</Badge>
                      </TableCell>
                      <TableCell className="max-w-[150px] truncate" title={v.original_filename}>
                        {v.original_filename}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {v.mime_type}
                      </TableCell>
                      <TableCell>{v.uploaded_by_name || 'Unknown'}</TableCell>
                      <TableCell className="text-sm">
                        {format(new Date(v.uploaded_at), 'd MMM yyyy HH:mm')}
                      </TableCell>
                      <TableCell>
                        {v.file_storage_key && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleFileDownload(v.id, v.original_filename)}
                            data-testid={`download-version-${v.id}`}
                          >
                            <Download className="h-3.5 w-3.5 mr-1" />
                            Download
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setVersionsDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
